import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
  Fragment,
} from 'react';
import { Separator } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';

import { useGetInternalMessages, usePostInternalMessage } from 'actions/internal-tickets';
import { getPubnub, subscribe } from 'screens/support-agent/pubnub-manager';
import { mapBackendMessage, getDateLabel } from 'screens/support-agent/utils';
import ThreadItem from 'screens/support-agent/components/thread-item';
import Icon from 'screens/support-agent/components/icon';
import {
  ERROR_AUTO_CLEAR_MS,
  TICKET_STATUS_CHANGE_EVENTS,
} from 'screens/support-agent/constants';

import { SIMPLIFIED_STATUS, LOCKED_STATUSES } from '../constants';
import {
  ThreadContainer,
  ThreadHeaderBar,
  ThreadHeaderTitle,
  ThreadHeaderStatus,
  ThreadScroll,
  ThreadInner,
  DateSeparator,
  DateSeparatorLabel,
  ComposerWrap,
  ComposerTextarea,
  ComposerRow,
  ComposerHint,
  SendBtn,
  ErrorMsg,
  LoadOlderBtn,
} from '../styled-comps';

const mapPubnubMessage = msg =>
  mapBackendMessage({ ...msg, timestamp: msg.timestamp || msg.at });

const PAGE_SIZE = 30;

const ThreadView = ({ ticketId, ticketDetail, detailError, onRefreshDetail }) => {
  const [items, setItems] = useState([]);
  const [val, setVal] = useState('');
  const [threadError, setThreadError] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const scrollRef = useRef(null);
  const prependAnchorRef = useRef(null);
  const initialLoadRef = useRef(false);

  useEffect(() => {
    if (!threadError) return;
    const timer = setTimeout(() => setThreadError(null), ERROR_AUTO_CLEAR_MS);
    return () => clearTimeout(timer);
  }, [threadError]);

  // Single in-flight send (composer disabled while sending), so one ref is
  // enough to identify the bubble to roll back — onComplete omits the temp_id.
  const lastTempIdRef = useRef(null);
  const { post: postMessage } = usePostInternalMessage({
    onComplete: error => {
      setIsSending(false);
      if (error) {
        setThreadError(typeof error === 'string' ? error : 'Failed to send message');
        const failedId = lastTempIdRef.current;
        lastTempIdRef.current = null;
        if (failedId) {
          setItems(prev => prev.filter(i => i.id !== failedId));
        }
      } else {
        lastTempIdRef.current = null;
      }
    },
  });

  const {
    data: olderData,
    getAll: getOlderMessages,
    isProcessing: loadingOlder,
    isProcessingFailed: olderMsgsFailed,
  } = useGetInternalMessages();

  useEffect(() => {
    if (ticketId) {
      initialLoadRef.current = true;
      setItems([]);
      setCursor(null);
      setHasMore(false);
      setVal('');
      getOlderMessages(ticketId, { limit: PAGE_SIZE });
    }
  }, [ticketId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (olderMsgsFailed) {
      setThreadError(
        initialLoadRef.current
          ? 'Failed to load messages.'
          : 'Failed to load older messages.',
      );
    }
  }, [olderMsgsFailed]);

  useEffect(() => {
    if (loadingOlder || !olderData) return;

    // Advance the cursor even on an empty page so "Load older" can't loop.
    setCursor(olderData.next_cursor || null);
    setHasMore(olderData.has_more || false);

    const olderItems = olderData.items;
    if (!Array.isArray(olderItems) || olderItems.length === 0) {
      // empty first page must still clear the flag, else a later "Load older"
      // takes the REPLACE branch and discards live PubNub messages.
      initialLoadRef.current = false;
      return;
    }
    const mapped = olderItems.map(mapBackendMessage);

    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      setItems(mapped);
      return;
    }

    const el = scrollRef.current;
    prependAnchorRef.current = el
      ? { scrollTop: el.scrollTop, scrollHeight: el.scrollHeight }
      : null;
    setItems(prev => {
      const mappedIds = new Set(mapped.map(i => i.id));
      const onlyInPrev = prev.filter(i => !mappedIds.has(i.id));
      return [...mapped, ...onlyInPrev];
    });
  }, [olderData, loadingOlder]);

  // Must stay a layout effect: restoring from a requestAnimationFrame instead
  // races the passive effect that follows the conversation down, and the rAF
  // loses -- the thread snaps to the bottom right after older messages load.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const anchor = prependAnchorRef.current;
    if (anchor) {
      prependAnchorRef.current = null;
      el.scrollTop = anchor.scrollTop + el.scrollHeight - anchor.scrollHeight;
      return;
    }

    el.scrollTop = el.scrollHeight;
  }, [items]);

  useEffect(() => {
    const pubnubChannel = ticketDetail?.pubnub_channel;
    if (!pubnubChannel) return;

    const pubnub = getPubnub();
    if (!pubnub) return;

    subscribe([pubnubChannel]);

    const listener = {
      message: event => {
        if (event.channel !== pubnubChannel) return;
        const msg = event.message;

        // Checked before the display filter so a status event delivered as a
        // system message still triggers a refetch.
        if (
          TICKET_STATUS_CHANGE_EVENTS.includes(msg.system_event_type) &&
          onRefreshDetail
        ) {
          onRefreshDetail();
        }

        const type = (msg.type || msg.item_type || '').toUpperCase();
        if (type === 'NOTE' || type === 'SYSTEM_INTERNAL_MESSAGE') return;

        const newMsg = mapPubnubMessage(msg);
        setItems(prev => {
          // replace the optimistic temp item with the server message so it
          // adopts the real message_id + rawTs (avoids a later double-render).
          if (msg.temp_id) {
            const idx = prev.findIndex(i => i.id === msg.temp_id);
            if (idx !== -1) {
              const next = [...prev];
              next[idx] = newMsg;
              return next;
            }
          }
          if (msg.message_id && prev.find(i => i.id === msg.message_id)) return prev;
          return [...prev, newMsg];
        });
      },
    };
    pubnub.addListener(listener);

    return () => {
      pubnub.removeListener(listener);
      pubnub.unsubscribe({ channels: [pubnubChannel] });
    };
  }, [ticketDetail?.pubnub_channel]);

  const pushOptimistic = useCallback(
    (body, extra) => {
      const ts = new Date().toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });

      const tempId = crypto.randomUUID();
      lastTempIdRef.current = tempId;
      setIsSending(true);

      postMessage(
        { content: body, message_type: 'TEXT', temp_id: tempId, ...extra },
        ticketId,
      );

      setItems(prev => [
        ...prev,
        {
          id: tempId,
          kind: 'msg',
          who: 'user',
          name: ticketDetail?.customer_name || 'User',
          body,
          ts,
          rawTs: new Date().toISOString(),
          messageType: 'TEXT',
          mediaUrl: null,
        },
      ]);
    },
    [ticketId, ticketDetail, postMessage],
  );

  const send = useCallback(() => {
    const trimmed = val.trim();
    if (!trimmed || isSending) return;
    pushOptimistic(trimmed);
    setVal('');
  }, [val, isSending, pushOptimistic]);

  const handleOptionSelect = useCallback(
    opt => {
      if (isSending) return;
      const label = opt.label || opt.value || '';
      if (!label) return;
      pushOptimistic(label, {
        selected_disposition: opt.path || opt.value || undefined,
      });
    },
    [isSending, pushOptimistic],
  );

  const handleKeyDown = useCallback(
    e => {
      if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
      if (e.shiftKey) return;
      e.preventDefault();
      send();
    },
    [send],
  );

  const handleLoadOlder = useCallback(() => {
    if (ticketId && cursor && hasMore && !loadingOlder) {
      getOlderMessages(ticketId, { before: cursor, limit: PAGE_SIZE });
    }
  }, [ticketId, cursor, hasMore, loadingOlder, getOlderMessages]);

  const visibleItems = useMemo(
    () =>
      items.filter(i => i.kind === 'msg' || i.kind === 'system' || i.kind === 'event'),
    [items],
  );

  const dateLabels = useMemo(() => {
    const labels = [];
    let prevLabel = null;
    for (let i = 0; i < visibleItems.length; i++) {
      const label = getDateLabel(visibleItems[i].rawTs);
      labels.push(label && label !== prevLabel ? label : null);
      if (label) prevLabel = label;
    }
    return labels;
  }, [visibleItems]);

  const status = (ticketDetail?.status || '').toUpperCase();
  const isLocked = LOCKED_STATUSES.includes(status);

  return (
    <ThreadContainer>
      <ThreadHeaderBar>
        <Icon name="chat" size={20} />
        <ThreadHeaderTitle>
          <Text variant="body1Medium">
            #{(ticketId || '').slice(0, 8)} — {ticketDetail?.customer_name || 'User'}
          </Text>
          <Separator height={2} transparent />
          <ThreadHeaderStatus variant="caption">
            {SIMPLIFIED_STATUS[status] || status}
            {ticketDetail?.category ? ` — ${ticketDetail.category}` : ''}
          </ThreadHeaderStatus>
        </ThreadHeaderTitle>
      </ThreadHeaderBar>

      <ThreadScroll ref={scrollRef}>
        <ThreadInner>
          {loadingOlder && initialLoadRef.current && (
            <DateSeparator>
              <DateSeparatorLabel>Loading messages...</DateSeparatorLabel>
            </DateSeparator>
          )}
          {hasMore && (
            <LoadOlderBtn onClick={handleLoadOlder} disabled={loadingOlder}>
              {loadingOlder ? 'Loading...' : 'Load older messages'}
            </LoadOlderBtn>
          )}
          {visibleItems.map((item, idx) => (
            <Fragment key={item.id || `${item.kind}-${idx}`}>
              {dateLabels[idx] && (
                <DateSeparator>
                  <DateSeparatorLabel>{dateLabels[idx]}</DateSeparatorLabel>
                </DateSeparator>
              )}
              <ThreadItem
                item={item}
                userOnRight
                onOptionSelect={
                  idx === visibleItems.length - 1 ? handleOptionSelect : undefined
                }
              />
            </Fragment>
          ))}
          <Separator height={40} transparent />
        </ThreadInner>
      </ThreadScroll>

      {(threadError || detailError) && (
        <ErrorMsg>
          {threadError ||
            (typeof detailError === 'string'
              ? detailError
              : 'Failed to load ticket details.')}
        </ErrorMsg>
      )}

      {!isLocked && (
        <ComposerWrap>
          <ComposerTextarea
            value={val}
            onChange={e => setVal(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message as the user..."
            rows={2}
          />
          <ComposerRow>
            <ComposerHint variant="caption">
              Enter to send, Shift+Enter for new line
            </ComposerHint>
            <SendBtn onClick={send} disabled={!val.trim() || isSending}>
              <Icon name="send" size={16} />
            </SendBtn>
          </ComposerRow>
        </ComposerWrap>
      )}
    </ThreadContainer>
  );
};

export default ThreadView;
